import Input from "../inputs/Input";
import { AiOutlineLoading } from "react-icons/ai";
import { Alert, Button } from "flowbite-react";
import useCEP from "../../hooks/useCEP";
import { Controller, useForm } from "react-hook-form";
import { AVATAR, PROFESSIONALS, CLIENTS } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import { useEffect, useState, useMemo } from "react";
import CPFInput from "../inputs/CPFInput";
import PhoneInput from "../inputs/PhoneInput";
import { resetValuesForm, toBooleanFlag } from "../../utils/utils";
import SelectInput from "../inputs/SelectInput";
import { HiInformationCircle } from "react-icons/hi";
import ImageInput from "../inputs/ImageInput";
import { useQuery, useQueryClient } from "react-query";
import { formatCurrencyBRL } from "../../utils/utils";
import {
  FiChevronDown,
  FiChevronUp,
  FiScissors,
  FiShoppingBag,
} from "react-icons/fi";
import FormTabs from "./FormTabs";
import {
  MdOutlineBadge,
  MdOutlinePercent,
  MdOutlineTune,
} from "react-icons/md";

const weekdayLabels = [
  "Domingo",
  "Segunda",
  "Terça",
  "Quarta",
  "Quinta",
  "Sexta",
  "Sábado",
];

function timeToMinutes(timeStr) {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

function formatCurrencyInputValue(value) {
  if (value === undefined || value === null || value === "") {
    return "";
  }

  if (Number.isNaN(Number(value))) {
    return "";
  }

  return formatCurrencyBRL.format(Number(value));
}

function parseCurrencyInputValue(value) {
  if (!value) return "";

  const onlyDigits = (value + "").replace(/\D/g, "");
  if (!onlyDigits) return "";

  return Number(onlyDigits) / 100;
}

const PrettySwitch = ({
  checked,
  onChange,
  disabled = false,
  size = "md",
  ariaLabel,
}) => {
  const sizeClasses =
    size === "sm"
      ? {
          track: "h-6 w-12",
          knob: "h-5 w-5",
          translate: "translate-x-5",
        }
      : {
          track: "h-7 w-14",
          knob: "h-6 w-6",
          translate: "translate-x-6",
        };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => !disabled && onChange?.()}
      className={`inline-flex items-center rounded-full px-1.5 py-0.5 transition-all focus:outline-none focus:ring-2 focus:ring-rose-300 ${
        checked
          ? "bg-gradient-to-r from-rose-500 to-pink-500 shadow-[0_6px_16px_rgba(236,72,153,0.35)]"
          : "bg-slate-200"
      } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`relative rounded-full ${sizeClasses.track} transition-colors`}
      >
        <span
          className={`absolute left-3 top-0.5 rounded-full bg-white shadow-md transition-transform ${sizeClasses.knob} ${
            checked ? sizeClasses.translate : "-translate-x-3"
          }`}
        />
      </span>
    </button>
  );
};

const PROFESSIONAL_TABS = [
  { key: "info", label: "Informações", icon: MdOutlineBadge },
  { key: "commissions", label: "Comissões", icon: MdOutlinePercent },
  { key: "settings", label: "Configurações", icon: MdOutlineTune },
];

/*
  Aba de cada campo obrigatório — é o que faz a aba acender quando a validação
  reprova algo fora da vista. Sem isso, clicar em "Atualizar" com o e-mail em
  branco (aba Informações) enquanto se olha Comissões não produzia efeito
  visível nenhum.
*/
const TAB_BY_FIELD = {
  name: "info",
  surname: "info",
  email: "info",
  phoneNumber: "info",
  cpf: "info",
  permissionId: "info",
  percentageCommission: "commissions",
};

const ProfessionalForm = ({
  professional,
  fetchProfessionals,
  onClose,
  isOpen,
  type = "CREATE",
  totalProfessionalsCount = 0,
}) => {
  // No back-office quem edita é o suporte, que não pertence à empresa do
  // profissional — todo o escopo de dados sai do próprio cadastro.
  const companyId = professional?.companyId;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [avatarImg, setAvatarImg] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [imageKey, setImageKey] = useState(0);
  const [activeTab, setActiveTab] = useState("info"); // "info" | "settings" | "commissions"
  const [serviceSearch, setServiceSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [collapsedCategories, setCollapsedCategories] = useState({});
  // % digitada no cabeçalho de cada categoria (não é salva: é só o valor a
  // aplicar em lote nos serviços daquela categoria).
  const [categoryCommissionInputs, setCategoryCommissionInputs] = useState({});

  // Horários de atendimento do profissional
  const [workingHoursForm, setWorkingHoursForm] = useState([]);
  const [loadingWorkingHours, setLoadingWorkingHours] = useState(false);
  const [serviceCommissions, setServiceCommissions] = useState([]);
  const [productCommissions, setProductCommissions] = useState([]);
  const queryClient = useQueryClient();

  const {
    register,
    getValues,
    handleSubmit,
    reset,
    setValue,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      isActive: true,
      isActiveCalendar: true,
      canViewRevenue: false,
      suggestionIntervalMinutes: 30,
      appointmentSpacingMinutes: 0,
    },
  });

  const {
    setCep,
    address,
    loading: loadingCEP,
    error: errorCEP,
  } = useCEP();

  // O suporte sempre atua com poderes de administrador.
  const isAdminOrPower = true;
  const isSingleProfessionalEdit =
    type === "EDIT" && totalProfessionalsCount === 1;

  // Serviços, produtos e categorias da empresa do profissional. O back-office
  // não tem endpoints por empresa separados, então tudo vem de um só catálogo.
  const { data: catalog, isLoading: isLoadingCatalog } = useQuery(
    ["professional-form-catalog", companyId],
    async () => {
      const { data: response } = await axios.get(
        `${CLIENTS.CATALOG}/${companyId}/catalog`,
      );

      return response?.data || {};
    },
    { enabled: Boolean(companyId) },
  );

  const services = useMemo(
    () =>
      [...(catalog?.services || [])].sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    [catalog],
  );
  const products = useMemo(
    () =>
      [...(catalog?.products || [])].sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    [catalog],
  );
  const categories = useMemo(() => catalog?.categories || [], [catalog]);
  const isLoadingServices = isLoadingCatalog;
  const isLoadingProducts = isLoadingCatalog;

  /* ===========================
   * Carregar / resetar dados
   * =========================== */
  useEffect(() => {
    if (type === "EDIT" && professional) {
      /*
        O banco devolve `null` para campo de texto vazio, e espalhar o
        registro cru punha esse `null` dentro do formulário. Campo com máscara
        (telefone, CPF) chama `.split()` no valor e derrubava a TELA INTEIRA
        com "Cannot read properties of null" — bastava abrir o cadastro de um
        usuário sem telefone.

        A conversão acontece aqui, na entrada, e não em cada input: um campo
        novo nasce protegido sem ninguém precisar lembrar.
      */
      const semNulos = Object.fromEntries(
        Object.entries(professional).map(([chave, valor]) => [
          chave,
          valor === null ? "" : valor,
        ])
      );

      const base = {
        ...semNulos,
        isActive: toBooleanFlag(professional?.isActive, true),
        isActiveCalendar: toBooleanFlag(professional?.isActiveCalendar, true),
        canViewRevenue: toBooleanFlag(professional?.canViewRevenue, false),
        suggestionIntervalMinutes:
          professional?.suggestionIntervalMinutes ?? 30,
        appointmentSpacingMinutes: professional?.appointmentSpacingMinutes ?? 0,
      };
      reset(base);
      setPreviewUrl(professional?.imageUrl || "");
      setAvatarImg(professional?.avatar || "");
      setImageKey((prev) => prev + 1);
    } else if (type === "CREATE") {
      resetValuesForm(["cpf", "phoneNumber"], setValue, reset);
      setPreviewUrl("");
      setAvatarImg("");
      reset({
        isActive: true,
        isActiveCalendar: true,
        canViewRevenue: false,
        suggestionIntervalMinutes: 30,
        appointmentSpacingMinutes: 0,
      });
      setServiceCommissions([]);
      setProductCommissions([]);
    }

    setValue("password", "");
    setError(null);
    setActiveTab("info");

    if (type === "EDIT" && totalProfessionalsCount === 1) {
      setValue("permissionId", "ADMIN");
    }
  }, [professional, isOpen, type, reset, setValue, totalProfessionalsCount]);

  useEffect(() => {
    if (!services.length) return;

    if (type === "CREATE") {
      setServiceCommissions(
        services.map((service) => ({
          serviceId: service.id,
          isCheck: false,
          commissionPercentage: Number(watch("percentageCommission") || 0),
          commissionPercentageAsAuxiliary: Number(
            watch("percentageCommissionAsAuxiliary") || 0,
          ),
          commissionPercentageWithAuxiliary: Number(
            watch("percentageCommissionWithAuxiliary") || 0,
          ),
          priceUnit: Number(service.price || 0),
        })),
      );
      return;
    }

    if (type === "EDIT" && professional?.id) {
      const loadProfessionalServices = async () => {
        try {
          const { data: response } = await axios.get(
            `${PROFESSIONALS.ONE}/${professional.id}/services`,
          );
          const professionalServices = response?.data || [];
          const byServiceId = new Map(
            professionalServices.map((item) => [
              Number(item.ServiceId),
              {
                commissionPercentage: Number(item.commissionPercentage || 0),
                commissionPercentageAsAuxiliary: Number(
                  item.commissionPercentageAsAuxiliary || 0,
                ),
                commissionPercentageWithAuxiliary: Number(
                  item.commissionPercentageWithAuxiliary || 0,
                ),
                priceUnit:
                  item.priceUnit !== undefined && item.priceUnit !== null
                    ? Number(item.priceUnit)
                    : null,
              },
            ]),
          );

          setServiceCommissions(
            services.map((service) => ({
              serviceId: service.id,
              isCheck: byServiceId.has(Number(service.id)),
              commissionPercentage: byServiceId.has(Number(service.id))
                ? Number(
                    byServiceId.get(Number(service.id))?.commissionPercentage ||
                      0,
                  )
                : Number(watch("percentageCommission") || 0),
              commissionPercentageAsAuxiliary: byServiceId.has(
                Number(service.id),
              )
                ? Number(
                    byServiceId.get(Number(service.id))
                      ?.commissionPercentageAsAuxiliary || 0,
                  )
                : Number(watch("percentageCommissionAsAuxiliary") || 0),
              commissionPercentageWithAuxiliary: byServiceId.has(
                Number(service.id),
              )
                ? Number(
                    byServiceId.get(Number(service.id))
                      ?.commissionPercentageWithAuxiliary || 0,
                  )
                : Number(watch("percentageCommissionWithAuxiliary") || 0),
              priceUnit: byServiceId.has(Number(service.id))
                ? (byServiceId.get(Number(service.id))?.priceUnit ??
                  Number(service.price || 0))
                : Number(service.price || 0),
            })),
          );
        } catch (err) {
          setServiceCommissions(
            services.map((service) => ({
              serviceId: service.id,
              isCheck: false,
              commissionPercentage: Number(watch("percentageCommission") || 0),
              commissionPercentageAsAuxiliary: Number(
                watch("percentageCommissionAsAuxiliary") || 0,
              ),
              commissionPercentageWithAuxiliary: Number(
                watch("percentageCommissionWithAuxiliary") || 0,
              ),
              priceUnit: Number(service.price || 0),
            })),
          );
        }
      };

      loadProfessionalServices();
    }
  }, [services, type, professional?.id]);

  useEffect(() => {
    if (!products.length) return;

    if (type === "CREATE") {
      setProductCommissions(
        products.map((product) => ({
          productId: product.id,
          isCheck: false,
          commissionPercentage: Number(watch("percentageCommission") || 0),
          priceUnit: Number(product.price || 0),
        })),
      );
      return;
    }

    if (type === "EDIT" && professional?.id) {
      const loadProfessionalProducts = async () => {
        try {
          const { data: response } = await axios.get(
            `${PROFESSIONALS.ONE}/${professional.id}/products`,
          );
          const professionalProducts = response?.data || [];
          const byProductId = new Map(
            professionalProducts.map((item) => [
              Number(item.ProductId),
              {
                commissionPercentage: Number(item.commissionPercentage || 0),
                priceUnit:
                  item.priceUnit !== undefined && item.priceUnit !== null
                    ? Number(item.priceUnit)
                    : null,
              },
            ]),
          );

          setProductCommissions(
            products.map((product) => ({
              productId: product.id,
              isCheck: byProductId.has(Number(product.id)),
              commissionPercentage: byProductId.has(Number(product.id))
                ? Number(
                    byProductId.get(Number(product.id))?.commissionPercentage ||
                      0,
                  )
                : Number(watch("percentageCommission") || 0),
              priceUnit: byProductId.has(Number(product.id))
                ? (byProductId.get(Number(product.id))?.priceUnit ??
                  Number(product.price || 0))
                : Number(product.price || 0),
            })),
          );
        } catch (err) {
          setProductCommissions(
            products.map((product) => ({
              productId: product.id,
              isCheck: false,
              commissionPercentage: Number(watch("percentageCommission") || 0),
              priceUnit: Number(product.price || 0),
            })),
          );
        }
      };

      loadProfessionalProducts();
    }
  }, [products, type, professional?.id]);

  /* ===========================
   * CEP → endereço
   * =========================== */
  useEffect(() => {
    if (!address) return;
    setValue("address", address?.logradouro || "");
    setValue("addressNeighborhood", address?.bairro || "");
    setValue("addressCity", address?.localidade || "");
  }, [address, setValue]);

  const onClickSearchCEP = () => {
    const cepValue = getValues("addressPostalCode");
    if (!cepValue) return;
    setCep(cepValue);
  };

  /* ===========================
   * Carregar Working Hours
   * =========================== */

  const normalizeWorkingHours = (items = []) => {
    return items
      .sort((a, b) => a.weekday - b.weekday)
      .map((wh) => ({
        id: wh.id,
        weekday: wh.weekday,
        isOpen: !!wh.isOpen,
        startHour: wh.startHour ? wh.startHour.slice(0, 5) : "",
        endHour: wh.endHour ? wh.endHour.slice(0, 5) : "",
        breakStart: wh.breakStart ? wh.breakStart.slice(0, 5) : "",
        breakEnd: wh.breakEnd ? wh.breakEnd.slice(0, 5) : "",
      }));
  };

  const ensureFullWeek = (items = []) => {
    const mapByDay = new Map(items.map((it) => [it.weekday, it]));
    const result = [];

    for (let d = 0; d < 7; d++) {
      if (mapByDay.has(d)) {
        result.push(mapByDay.get(d));
      } else {
        result.push({
          id: null,
          weekday: d,
          isOpen: false,
          startHour: "",
          endHour: "",
          breakStart: "",
          breakEnd: "",
        });
      }
    }

    return result;
  };

  const loadWorkingHours = async () => {
    if (!companyId) return;
    setLoadingWorkingHours(true);

    try {
      // 1) horários da empresa
      const { data: companyResp } = await axios.get(
        `${CLIENTS.WORKING_HOURS}/${companyId}/working-hours`,
      );
      const companyItems = normalizeWorkingHours(companyResp?.data || []);

      let base = ensureFullWeek(companyItems);

      // 2) se for edição, tenta buscar horários do profissional
      if (type === "EDIT" && professional?.id) {
        const { data: proResp } = await axios.get(
          `${PROFESSIONALS.ONE}/${professional.id}/working-hours`,
        );

        if (proResp?.data && proResp.data.length > 0) {
          const proItems = normalizeWorkingHours(proResp.data);
          base = ensureFullWeek(proItems);
        }
      }

      setWorkingHoursForm(base);
    } catch (err) {
      console.error("Erro ao carregar working hours do profissional:", err);
      // fallback: semana vazia
      setWorkingHoursForm(
        ensureFullWeek([
          // se nada vier, vai gerar todos fechados
        ]),
      );
    } finally {
      setLoadingWorkingHours(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    loadWorkingHours();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, type, professional?.id, companyId]);

  /* ===========================
   * Helpers de envio
   * =========================== */

  const handleAvatarUpload = async (data) => {
    if (!previewUrl || !previewUrl.includes("data:image/")) return data;

    const formData = new FormData();
    formData.append("image", avatarImg);

    const { data: avatar } = await axios.post(
      `${AVATAR.UPLOAD}?avatar=${professional?.avatar || ""}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    return {
      ...data,
      avatar: avatar.imageName,
    };
  };

  const validateEmailIfChanged = async (data) => {
    if (professional?.email && professional.email === data.email) return;

    // Este endpoint do back-office responde 404 quando o e-mail JÁ existe.
    try {
      await axios.get(`${PROFESSIONALS.BY_EMAIL}/${data.email}`);
    } catch (requestError) {
      if (requestError?.response?.status === 404) {
        throw new Error("E-mail já cadastrado");
      }

      throw requestError;
    }
  };

  const saveWorkingHoursForProfessional = async (professionalId) => {
    if (!professionalId || workingHoursForm.length === 0) return;

    await axios.put(
      `${PROFESSIONALS.ONE}/${professionalId}/working-hours`,
      {
        WorkingHours: workingHoursForm,
      },
    );
  };

  const saveServicesCommissionForProfessional = async (
    professionalId,
    data,
  ) => {
    if (!professionalId) return;

    const defaultCommission = Number(data?.percentageCommission || 0);
    const defaultCommissionAsAuxiliary = Number(
      data?.percentageCommissionAsAuxiliary || 0,
    );
    const defaultCommissionWithAuxiliary = Number(
      data?.percentageCommissionWithAuxiliary || 0,
    );
    const payloadServices = serviceCommissions.map((service) => ({
      serviceId: service.serviceId,
      isCheck: Boolean(service.isCheck),
      commissionPercentage: Number(
        service.commissionPercentage ?? defaultCommission,
      ),
      commissionPercentageAsAuxiliary: Number(
        service.commissionPercentageAsAuxiliary ?? defaultCommissionAsAuxiliary,
      ),
      commissionPercentageWithAuxiliary: Number(
        service.commissionPercentageWithAuxiliary ??
          defaultCommissionWithAuxiliary,
      ),
      priceUnit: service.priceUnit,
    }));

    await axios.put(
      `${PROFESSIONALS.ONE}/${professionalId}/services`,
      { services: payloadServices },
    );
  };

  const saveProductsCommissionForProfessional = async (
    professionalId,
    data,
  ) => {
    if (!professionalId) return;

    const defaultCommission = Number(data?.percentageCommission || 0);
    const payloadProducts = productCommissions.map((product) => ({
      productId: product.productId,
      isCheck: Boolean(product.isCheck),
      commissionPercentage: Number(
        product.commissionPercentage ?? defaultCommission,
      ),
      priceUnit: product.priceUnit,
    }));

    await axios.put(
      `${PROFESSIONALS.ONE}/${professionalId}/products`,
      { products: payloadProducts },
    );
  };

  const handleServiceToggle = (serviceId) => {
    setServiceCommissions((prev) =>
      prev.map((item) =>
        Number(item.serviceId) === Number(serviceId)
          ? { ...item, isCheck: !item.isCheck }
          : item,
      ),
    );
  };

  const handleServiceCommissionChange = (
    serviceId,
    value,
    field = "commissionPercentage",
  ) => {
    setServiceCommissions((prev) =>
      prev.map((item) => {
        if (Number(item.serviceId) !== Number(serviceId)) return item;

        const normalized = String(value || "").replace(",", ".");
        return {
          ...item,
          [field]: normalized === "" ? "" : normalized,
        };
      }),
    );
  };

  const handleProductToggle = (productId) => {
    setProductCommissions((prev) =>
      prev.map((item) =>
        Number(item.productId) === Number(productId)
          ? { ...item, isCheck: !item.isCheck }
          : item,
      ),
    );
  };

  const handleProductCommissionChange = (productId, value) => {
    setProductCommissions((prev) =>
      prev.map((item) => {
        if (Number(item.productId) !== Number(productId)) return item;

        const normalized = String(value || "").replace(",", ".");
        return {
          ...item,
          commissionPercentage: normalized === "" ? "" : normalized,
        };
      }),
    );
  };

  const handleProductValueChange = (
    productId,
    value,
    field = "commissionPercentage",
  ) => {
    setProductCommissions((prev) =>
      prev.map((item) => {
        if (Number(item.productId) !== Number(productId)) return item;

        const normalized = String(value || "").replace(",", ".");
        return {
          ...item,
          [field]: normalized === "" ? "" : normalized,
        };
      }),
    );
  };

  const filteredServices = useMemo(() => {
    const search = String(serviceSearch || "")
      .trim()
      .toLowerCase();
    if (!search) return services;

    return services.filter((service) =>
      String(service.name || "")
        .toLowerCase()
        .includes(search),
    );
  }, [services, serviceSearch]);

  const categoryNameById = useMemo(() => {
    const map = new Map();
    categories.forEach((category) => {
      map.set(Number(category.id), category.name);
    });
    return map;
  }, [categories]);

  const serviceCommissionById = useMemo(() => {
    const map = new Map();
    serviceCommissions.forEach((item) => {
      map.set(Number(item.serviceId), item);
    });
    return map;
  }, [serviceCommissions]);

  const groupedServices = useMemo(() => {
    const grouped = new Map();

    filteredServices.forEach((service) => {
      const categoryId = Number(service.categoryId || 0);
      const categoryLabel =
        service.categoryName ||
        categoryNameById.get(categoryId) ||
        "Sem categoria";
      const key = String(categoryId || "no-category");

      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          name: categoryLabel,
          services: [],
          activeCount: 0,
        });
      }

      const group = grouped.get(key);
      group.services.push(service);

      const serviceConfig = serviceCommissionById.get(Number(service.id));

      if (serviceConfig?.isCheck) {
        group.activeCount += 1;
      }
    });

    return Array.from(grouped.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [filteredServices, categoryNameById, serviceCommissionById]);

  useEffect(() => {
    if (!groupedServices.length) return;

    setCollapsedCategories((prev) => {
      const next = { ...prev };
      groupedServices.forEach((group) => {
        if (typeof next[group.key] === "undefined") {
          next[group.key] = true;
        }
      });
      return next;
    });
  }, [groupedServices]);

  const totalActiveServices = useMemo(
    () => serviceCommissions.filter((item) => Boolean(item.isCheck)).length,
    [serviceCommissions],
  );

  const productCommissionById = useMemo(() => {
    const map = new Map();
    productCommissions.forEach((item) => {
      map.set(Number(item.productId), item);
    });
    return map;
  }, [productCommissions]);

  const filteredProducts = useMemo(() => {
    const search = String(productSearch || "")
      .trim()
      .toLowerCase();

    if (!search) return products;

    return products.filter((product) =>
      String(product.name || "")
        .toLowerCase()
        .includes(search),
    );
  }, [products, productSearch]);

  const groupedProducts = useMemo(() => {
    const grouped = new Map();

    filteredProducts.forEach((product) => {
      const categoryId = Number(product.categoryId || 0);
      const categoryLabel =
        product.categoryName ||
        categoryNameById.get(categoryId) ||
        "Sem categoria";
      const key = `product-${String(categoryId || "no-category")}`;

      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          name: categoryLabel,
          products: [],
          activeCount: 0,
        });
      }

      const group = grouped.get(key);
      group.products.push(product);

      const productConfig = productCommissionById.get(Number(product.id));

      if (productConfig?.isCheck) {
        group.activeCount += 1;
      }
    });

    return Array.from(grouped.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [filteredProducts, categoryNameById, productCommissionById]);

  useEffect(() => {
    if (!groupedProducts.length) return;

    setCollapsedCategories((prev) => {
      const next = { ...prev };
      groupedProducts.forEach((group) => {
        if (typeof next[group.key] === "undefined") {
          next[group.key] = true;
        }
      });
      return next;
    });
  }, [groupedProducts]);

  const totalActiveProducts = useMemo(
    () => productCommissions.filter((item) => Boolean(item.isCheck)).length,
    [productCommissions],
  );

  const handleCategoryCommissionInput = (categoryKey, value) => {
    setCategoryCommissionInputs((prev) => ({ ...prev, [categoryKey]: value }));
  };

  // Comissão da categoria inteira: com dezenas de serviços, preencher um a um
  // é inviável. Aplica só nos serviços ATIVOS do grupo — os inativos têm o
  // campo desabilitado, e ativar serviço é decisão separada (o switch
  // "Ativar todos" ao lado resolve isso em um clique). O grupo já respeita a
  // busca, então o lote atinge exatamente o que está na tela.
  const applyCategoryCommission = (group) => {
    const raw = String(categoryCommissionInputs[group.key] ?? "")
      .trim()
      .replace(",", ".");
    if (raw === "") return;

    const serviceIds = new Set(
      group.services.map((service) => Number(service.id)),
    );

    setServiceCommissions((prev) =>
      prev.map((item) =>
        serviceIds.has(Number(item.serviceId)) && item.isCheck
          ? { ...item, commissionPercentage: raw }
          : item,
      ),
    );
  };

  const toggleCategoryServices = (group, checked) => {
    const serviceIds = new Set(
      group.services.map((service) => Number(service.id)),
    );

    setServiceCommissions((prev) =>
      prev.map((item) =>
        serviceIds.has(Number(item.serviceId))
          ? { ...item, isCheck: checked }
          : item,
      ),
    );
  };

  const toggleCategoryCollapse = (categoryKey) => {
    setCollapsedCategories((prev) => ({
      ...prev,
      [categoryKey]: !prev[categoryKey],
    }));
  };

  const collapseAllCategories = (collapsed) => {
    setCollapsedCategories((prev) => {
      const next = { ...prev };
      groupedServices.forEach((group) => {
        next[group.key] = collapsed;
      });
      return next;
    });
  };

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      setError(null);

      const currentCalendarState = getValues("isActiveCalendar");

      data = {
        ...data,
        isActive: toBooleanFlag(data.isActive, true),
        isActiveCalendar: toBooleanFlag(currentCalendarState, true),
        canViewRevenue: toBooleanFlag(data.canViewRevenue, false),
      };

      data = await handleAvatarUpload(data);
      await validateEmailIfChanged(data);

      let professionalId = professional?.id;

      if (type === "CREATE") {
        const response = await axios.post(PROFESSIONALS.LIST, data);
        // tentar pegar id de forma defensiva
        professionalId =
          response?.data?.data?.id || response?.data?.id || professionalId;
      } else {
        if (!data.password) delete data.password;
        await axios.put(
          `${PROFESSIONALS.ONE}/${professional.id}`,
          data,
        );
        professionalId = professional.id;
      }

      // Salva horários / pausas do profissional
      if (professionalId) {
        await saveWorkingHoursForProfessional(professionalId);
        await saveServicesCommissionForProfessional(professionalId, data);
        await saveProductsCommissionForProfessional(professionalId, data);
      }

      await fetchProfessionals?.();
      await Promise.all([
        queryClient.invalidateQueries(["professionals"]),
        queryClient.invalidateQueries(["professionalsAppointment"]),
        queryClient.invalidateQueries(["professionals-active"]),
        queryClient.invalidateQueries(["professionals-delete-targets"]),
      ]);
      onClose?.(false);

      if (type === "CREATE") {
        resetValuesForm(["cpf", "phoneNumber"], setValue, reset);
        setPreviewUrl("");
        setAvatarImg("");
        reset({
          isActive: true,
          isActiveCalendar: true,
          canViewRevenue: false,
          suggestionIntervalMinutes: 30,
          appointmentSpacingMinutes: 0,
        });
        // recarrega horários default da company para um eventual próximo cadastro
        await loadWorkingHours();
      }
    } catch (err) {
      setError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ===========================
   * Mensagem de erro
   * =========================== */
  const errorMessage = useMemo(() => {
    if (!error) return null;

    if (error.message && error.message.toLowerCase().includes("e-mail já")) {
      return "E-mail já cadastrado. Use outro e-mail para esse profissional.";
    }

    const method = error?.config?.method;
    if (method && method !== "get") {
      const action = method === "put" ? "atualizar" : "criar";
      return `Erro ao ${action} profissional. Tente novamente em alguns instantes.`;
    }

    return "Não foi possível salvar o profissional. Verifique os dados e tente novamente.";
  }, [error]);

  /* ===========================
   * Handlers de Working Hours
   * =========================== */

  const handleToggleDayOpen = (index) => {
    setWorkingHoursForm((prev) =>
      prev.map((wh, i) =>
        i === index
          ? {
              ...wh,
              isOpen: !wh.isOpen,
            }
          : wh,
      ),
    );
  };

  const handleWorkingHourChange = (index, field, value) => {
    setWorkingHoursForm((prev) =>
      prev.map((wh, i) => {
        if (i !== index) return wh;

        return { ...wh, [field]: value };
      }),
    );
  };

  // Quais abas têm campo obrigatório pendente agora.
  const errorsByTab = Object.keys(errors || {}).reduce((acc, campo) => {
    const aba = TAB_BY_FIELD[campo];
    if (aba) acc[aba] = true;
    return acc;
  }, {});

  return (
    <form className="mb-6" onSubmit={handleSubmit(onSubmit)}>
      {/* Avatar */}
      <div className="mb-5 flex flex-col items-center gap-2">
        <ImageInput
          key={imageKey}
          image={avatarImg}
          setImage={setAvatarImg}
          previewUrl={previewUrl}
          setPreviewUrl={setPreviewUrl}
        />
        <p className="text-[11px] text-gray-500 text-center max-w-xs">
          Adicione uma foto para que seus clientes reconheçam o profissional na
          hora de agendar.
        </p>
      </div>

      {/*
        Abas compartilhadas com o formulário de cliente. Eram três botões
        escritos à mão com estilo embutido e repetido — e, por não serem
        fixas, sumiam ao rolar uma aba longa como Comissões.
      */}
      <FormTabs
        tabs={PROFESSIONAL_TABS}
        activeTab={activeTab}
        onChange={setActiveTab}
        errorsByTab={errorsByTab}
      />

      {/* CARD DA ABA */}
      <div className="rounded-2xl border border-gray-200 bg-white/90 shadow-sm p-4 sm:p-5 space-y-5">
        {activeTab === "info" && (
          <>
            {/* IDENTIFICAÇÃO */}
            <section className="border-b border-gray-200 pb-4 mb-4">
              <p className="text-xs font-semibold text-gray-500 mb-4">
                Identificação
              </p>
              <div className="mb-4">
                <Input
                  type="text"
                  label="Nome *"
                  name="name"
                  required={true}
                  register={register}
                  error={errors?.name && "Este campo é obrigatório"}
                />
              </div>

              <div className="mb-2">
                <Input
                  type="email"
                  label="E-mail *"
                  name="email"
                  required={true}
                  register={register}
                  error={errors?.email && "Este campo é obrigatório"}
                />
                <small className="block mt-1 text-[11px] text-gray-500">
                  Esse será o e-mail utilizado para o login do profissional.
                </small>
              </div>

              {isAdminOrPower && (
                <div className="mt-4">
                  <Input
                    type="password"
                    label={`Senha ${
                      type === "CREATE"
                        ? "*"
                        : "(preencha apenas se quiser alterar)"
                    }`}
                    name="password"
                    required={type === "CREATE"}
                    register={register}
                  />
                  {type === "EDIT" && (
                    <small className="block mt-1 text-[11px] text-gray-500">
                      Se você não preencher esse campo, a senha atual será
                      mantida.
                    </small>
                  )}
                </div>
              )}
            </section>

            {/* CONTATO */}
            <section className="border-b border-gray-200 pb-4 mb-4">
              <p className="text-xs font-semibold text-gray-500 mb-4">
                Contato
              </p>
              <div className="mb-4">
                <PhoneInput
                  type="text"
                  label="Telefone *"
                  name="phoneNumber"
                  required={true}
                  register={register}
                  error={errors?.phoneNumber && "Este campo é obrigatório"}
                />
              </div>

              <div className="mb-2">
                <CPFInput
                  type="text"
                  label="CPF"
                  name="cpf"
                  required={false}
                  register={register}
                  value={watch("cpf") || ""}
                />
              </div>
            </section>

            {/* ENDEREÇO */}
            <section className="border-b border-gray-200 pb-4 mb-4">
              <p className="text-xs font-semibold text-gray-500 mb-4">
                Endereço (opcional)
              </p>

              <div className="mb-3 flex flex-col sm:flex-row gap-2">
                <Input
                  type="text"
                  label="CEP"
                  name="addressPostalCode"
                  required={false}
                  register={register}
                />
                <Button
                  type="button"
                  onClick={onClickSearchCEP}
                  className="flex items-center whitespace-nowrap primary"
                  style={{ minWidth: "150px", paddingTop: "4px" }}
                  isProcessing={loadingCEP}
                  processingSpinner={
                    <AiOutlineLoading className="h-5 w-5 animate-spin" />
                  }
                  disabled={loadingCEP}
                >
                  Buscar CEP
                </Button>
              </div>

              {errorCEP && (
                <p className="text-[11px] text-red-500 mb-2">
                  Não foi possível localizar o CEP. Verifique o número e tente
                  novamente.
                </p>
              )}

              <div className="mb-3">
                <Input
                  type="text"
                  label="Endereço"
                  name="address"
                  required={false}
                  register={register}
                />
              </div>

              <div className="mb-3 flex gap-4">
                <Input
                  type="text"
                  label="Número"
                  name="addressNumber"
                  required={false}
                  register={register}
                />
                <Input
                  type="text"
                  label="Bairro"
                  name="addressNeighborhood"
                  required={false}
                  register={register}
                />
              </div>

              <div className="flex gap-4">
                <Input
                  type="text"
                  label="Cidade"
                  name="addressCity"
                  required={false}
                  register={register}
                />
              </div>
            </section>

            {/* DADOS PESSOAIS */}
            <section>
              <p className="text-xs font-semibold text-gray-500 mb-4">
                Dados pessoais (opcional)
              </p>
              <div className="flex flex-col md:flex-row gap-4">
                <Input
                  type="date"
                  label="Data de nascimento"
                  name="birthday"
                  required={false}
                  register={register}
                />

                <div className="w-full relative">
                  <select
                    id="gender"
                    name="gender"
                    className="block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 transition placeholder:text-slate-400 focus:border-pink-500 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
                    {...register("gender")}
                    defaultValue={watch("gender") || ""}
                  >
                    <option value="">Selecione o gênero</option>
                    <option value="M">Masculino</option>
                    <option value="F">Feminino</option>
                  </select>
                  <label
                    htmlFor="gender"
                    className="absolute text-xs text-gray-500 bg-white px-1 -top-2 left-3"
                  >
                    Sexo
                  </label>
                </div>
              </div>
            </section>
          </>
        )}

        {activeTab === "settings" && (
          <>
            {/* APARÊNCIA */}
            <section className="border-b border-gray-200 pb-4 mb-4">
              <p className="text-xs font-semibold text-gray-500 mb-4">
                Aparência da agenda
              </p>
              <div className="flex flex-col md:flex-row gap-3">
                <SelectInput
                  label="Cor da agenda"
                  name="colorSchedule"
                  required={false}
                  register={register}
                >
                  <option value="#ff9ea4">Vermelho</option>
                  <option value="#9fc69a">Verde</option>
                  <option value="#eac198">Marrom</option>
                  <option value="#aae9ff">Azul</option>
                  <option value="#b7b7b7">Cinza</option>
                  <option value="#d8d497">Musgo</option>
                  <option value="#ffc9ea">Rosa</option>
                  <option value="#ffbf75">Laranja</option>
                  <option value="#d7a8ff">Roxo</option>
                  <option value="#e8e1a0">Amarelo</option>
                </SelectInput>

                {isAdminOrPower && (
                  <div className="w-full">
                    <SelectInput
                      label="Permissão"
                      name="permissionId"
                      required={true}
                      register={register}
                    >
                      <option value="ADMIN">Administrador</option>
                      {!isSingleProfessionalEdit && (
                        <>
                          <option value="USER">Usuário</option>
                          <option value="USER_CALENDARS">Recepcionista</option>
                        </>
                      )}
                    </SelectInput>
                    {isSingleProfessionalEdit && (
                      <p className="mt-1 text-[11px] text-slate-500">
                        Com apenas um profissional cadastrado, a permissão deve
                        permanecer como Administrador.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </section>

            {/* HORÁRIOS DE ATENDIMENTO */}
            <section className="border-b border-gray-200 pb-4 mb-4">
              <div className="mb-3">
                <SelectInput
                  label="Intervalo das sugestões de horários"
                  name="suggestionIntervalMinutes"
                  required={false}
                  register={register}
                >
                  <option value={15}>A cada 15 minutos</option>
                  <option value={30}>A cada 30 minutos</option>
                  <option value={60}>Apenas horários cheios (:00)</option>
                </SelectInput>
                <p className="mt-1 text-[11px] text-gray-500">
                  Define como os horários livres serão sugeridos para esse
                  profissional no agendamento.
                </p>
              </div>

              <div className="mb-3">
                <SelectInput
                  label="Espaçamento entre atendimentos"
                  name="appointmentSpacingMinutes"
                  required={false}
                  register={register}
                >
                  <option value={0}>Sem espaçamento (padrão)</option>
                  <option value={5}>5 minutos</option>
                  <option value={10}>10 minutos</option>
                  <option value={15}>15 minutos</option>
                  <option value={20}>20 minutos</option>
                  <option value={30}>30 minutos</option>
                </SelectInput>
                <p className="mt-1 text-[11px] text-gray-500">
                  Quando ativo, adiciona intervalo entre atendimentos para não
                  permitir encaixes colados.
                </p>
              </div>

              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-500">
                  Horários de atendimento
                </p>
                {loadingWorkingHours && (
                  <span className="text-[11px] text-gray-400 flex items-center gap-1">
                    <AiOutlineLoading className="h-3 w-3 animate-spin" />
                    Carregando horários...
                  </span>
                )}
              </div>
              <p className="text-[11px] text-gray-500 mb-3">
                Por padrão, usamos os horários configurados na empresa. Você
                pode personalizar os dias e horários deste profissional,
                incluindo pausas (como horário de almoço).
              </p>

              <div className="grid gap-3 md:grid-cols-2">
                {workingHoursForm.map((wh, index) => {
                  const start = timeToMinutes(wh.startHour);
                  const end = timeToMinutes(wh.endHour);
                  const bStart = timeToMinutes(wh.breakStart);
                  const bEnd = timeToMinutes(wh.breakEnd);
                  const isRangeInvalid =
                    start !== null && end !== null && end < start;
                  const isBreakInvalid =
                    bStart !== null && bEnd !== null && bEnd < bStart;

                  return (
                    <div
                      key={wh.weekday}
                      className="border border-gray-200 rounded-lg bg-slate-50/90 p-3 flex flex-col gap-2"
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="inline-flex items-center rounded-full px-3 py-0.5 text-xs font-semibold text-slate-800 border border-slate-200 bg-gray-50">
                          {weekdayLabels[wh.weekday]}
                        </span>

                        <PrettySwitch
                          checked={wh.isOpen}
                          onChange={() => handleToggleDayOpen(index)}
                          size="sm"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[11px] text-slate-600 mb-1">
                            Início
                          </label>
                          <input
                            type="time"
                            value={wh.startHour || ""}
                            onChange={(e) =>
                              handleWorkingHourChange(
                                index,
                                "startHour",
                                e.target.value,
                              )
                            }
                            disabled={!wh.isOpen}
                            className="block w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#bb2649] focus:border-[#bb2649]"
                            step={300}
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] text-slate-600 mb-1">
                            Fim
                          </label>
                          <input
                            type="time"
                            value={wh.endHour || ""}
                            onChange={(e) =>
                              handleWorkingHourChange(
                                index,
                                "endHour",
                                e.target.value,
                              )
                            }
                            disabled={!wh.isOpen}
                            className="block w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#bb2649] focus:border-[#bb2649]"
                            step={300}
                          />
                        </div>
                      </div>

                      {(isRangeInvalid || isBreakInvalid) && (
                        <div className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
                          {isRangeInvalid && (
                            <p>
                              O horário de fim deve ser depois do início do
                              atendimento.
                            </p>
                          )}
                          {isBreakInvalid && (
                            <p>O fim da pausa deve ser depois do início.</p>
                          )}
                        </div>
                      )}

                      <div className="mt-1 rounded-lg bg-white/80 border border-slate-200 px-2 py-2">
                        <p className="text-[11px] font-medium text-slate-600 mb-1">
                          Pausa (opcional)
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <label className="block text-[11px] text-slate-600">
                                Início da pausa
                              </label>
                              <button
                                type="button"
                                onClick={() =>
                                  handleWorkingHourChange(
                                    index,
                                    "breakStart",
                                    "",
                                  )
                                }
                                disabled={!wh.isOpen}
                                className="text-[10px] font-medium text-slate-500 hover:text-[#bb2649] disabled:opacity-50"
                              >
                                Limpar
                              </button>
                            </div>
                            <input
                              type="time"
                              value={wh.breakStart || ""}
                              onChange={(e) =>
                                handleWorkingHourChange(
                                  index,
                                  "breakStart",
                                  e.target.value,
                                )
                              }
                              disabled={!wh.isOpen}
                              className="block w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#bb2649] focus:border-[#bb2649]"
                              step={300}
                            />
                          </div>
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <label className="block text-[11px] text-slate-600">
                                Fim da pausa
                              </label>
                              <button
                                type="button"
                                onClick={() =>
                                  handleWorkingHourChange(index, "breakEnd", "")
                                }
                                disabled={!wh.isOpen}
                                className="text-[10px] font-medium text-slate-500 hover:text-[#bb2649] disabled:opacity-50"
                              >
                                Limpar
                              </button>
                            </div>
                            <input
                              type="time"
                              value={wh.breakEnd || ""}
                              onChange={(e) =>
                                handleWorkingHourChange(
                                  index,
                                  "breakEnd",
                                  e.target.value,
                                )
                              }
                              disabled={!wh.isOpen}
                              className="block w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#bb2649] focus:border-[#bb2649]"
                              step={300}
                            />
                          </div>
                        </div>
                        <p className="mt-1 text-[10px] text-slate-400">
                          Ex.: 12:00 às 13:00 para horário de almoço.
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* STATUS */}
            <section>
              <div className="mt-4">
                <p className="text-xs font-semibold text-gray-500 mb-2">
                  Visibilidade no agendamento
                </p>
              </div>
              <Controller
                control={control}
                name="isActiveCalendar"
                render={({ field }) => {
                  const currentValue = toBooleanFlag(field.value, true);

                  return (
                    <div className="flex items-center gap-3">
                      <PrettySwitch
                        checked={currentValue}
                        ariaLabel={
                          currentValue
                            ? "Desativar profissional no agendamento"
                            : "Ativar profissional no agendamento"
                        }
                        onChange={() => field.onChange(!currentValue)}
                      />
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900">
                          {currentValue
                            ? "Visível no agendamento"
                            : "Oculto do agendamento"}
                        </span>
                        <span className="text-[11px] text-gray-500">
                          {currentValue
                            ? "Este profissional aparece nas telas de agendamento."
                            : "Este profissional continua cadastrado, mas não aparece nas telas de agendamento."}
                        </span>
                      </div>
                    </div>
                  );
                }}
              />
            </section>
          </>
        )}

        {activeTab === "commissions" && (
          <>
            <section className="border-b border-gray-200 pb-4 mb-4">
              <p className="text-xs font-semibold text-gray-500 mb-3">
                Comissão padrão
              </p>
              <div className="mb-3">
                <Input
                  type="number"
                  label="Comissão base padrão (%)"
                  name="percentageCommission"
                  required={false}
                  register={register}
                  step="0.01"
                />
              </div>
              <div className="mb-3">
                <Input
                  type="number"
                  label="Comissão quando for auxiliar (%)"
                  name="percentageCommissionAsAuxiliary"
                  required={false}
                  register={register}
                  step="0.01"
                />
              </div>
              <div className="mb-2">
                <Input
                  type="number"
                  label="Comissão quando tiver auxiliar (%)"
                  name="percentageCommissionWithAuxiliary"
                  required={false}
                  register={register}
                  step="0.01"
                />
              </div>
              <p className="text-[11px] text-gray-500">
                Serviços com comissão específica continuam tendo prioridade. Sem
                comissão específica: usa taxa de auxiliar quando esse
                profissional atuar como auxiliar; usa taxa com auxiliar quando
                ele for o principal e houver auxiliar; caso contrário, usa a
                comissão base padrão.
              </p>
            </section>

            {/* O profissional já vê a própria comissão. Isto libera o passo
                seguinte: o quanto ele faturou para o salão — que é o preço
                cheio do serviço, informação que nem toda casa abre. Só faz
                sentido para permissão Usuário: Administrador e Recepcionista
                já enxergam o Financeiro inteiro. */}
            {watch("permissionId") === "USER" && (
              <section className="border-b border-gray-200 pb-4 mb-4">
                <p className="text-xs font-semibold text-gray-500 mb-3">
                  O que ele vê em "Minhas comissões"
                </p>
                <Controller
                  control={control}
                  name="canViewRevenue"
                  render={({ field }) => {
                    const currentValue = toBooleanFlag(field.value, false);

                    return (
                      <div className="flex items-center gap-3">
                        <PrettySwitch
                          checked={currentValue}
                          ariaLabel={
                            currentValue
                              ? "Ocultar o faturamento do profissional"
                              : "Mostrar o faturamento ao profissional"
                          }
                          onChange={() => field.onChange(!currentValue)}
                        />
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">
                            {currentValue
                              ? "Vê o total que faturou"
                              : "Vê apenas a própria comissão"}
                          </span>
                          <span className="text-[11px] text-gray-500">
                            {currentValue
                              ? "Além da comissão, aparece quanto ele gerou de faturamento para o salão no período."
                              : "A tela mostra só quanto ele tem a receber, sem o valor cheio dos serviços."}
                          </span>
                        </div>
                      </div>
                    );
                  }}
                />
              </section>
            )}

            <section>
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <div className="hidden md:flex h-8 w-8 items-center justify-center rounded-full border border-rose-100 bg-rose-50">
                    <FiScissors className="text-rose-700 text-base" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500">
                      Comissão por serviço
                    </p>
                    <p className="text-[11px] text-gray-500">
                      {totalActiveServices} ativo(s)
                    </p>
                  </div>
                </div>
                <span className="rounded-full bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700">
                  {groupedServices.reduce(
                    (acc, group) => acc + group.services.length,
                    0,
                  )}{" "}
                  total
                </span>
              </div>

              <p className="text-[11px] text-gray-500 mb-3">
                Ative os serviços atendidos por este profissional e ajuste a
                comissão e o preço específico de cada um.
              </p>

              <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                <div className="flex-1">
                  <Input
                    type="text"
                    label="Buscar serviço"
                    value={serviceSearch}
                    onChange={(e) => setServiceSearch(e.target.value)}
                  />
                </div>

                <div className="flex flex-wrap gap-2 lg:justify-end">
                  <Button
                    type="button"
                    size="xs"
                    color="light"
                    className="inline-flex items-center gap-2"
                    onClick={() => collapseAllCategories(false)}
                  >
                    <FiChevronDown className="text-sm" />
                    Abrir tudo
                  </Button>
                  <Button
                    type="button"
                    size="xs"
                    color="light"
                    className="inline-flex items-center gap-2"
                    onClick={() => collapseAllCategories(true)}
                  >
                    <FiChevronUp className="text-sm" />
                    Fechar tudo
                  </Button>
                </div>
              </div>

              {isLoadingServices ? (
                <div className="text-[11px] text-slate-500 flex items-center gap-1">
                  <AiOutlineLoading className="h-3 w-3 animate-spin" />
                  Carregando serviços...
                </div>
              ) : groupedServices.length > 0 ? (
                <div className="space-y-3 pr-1">
                  {groupedServices.map((group) => {
                    const isCollapsed = Boolean(collapsedCategories[group.key]);
                    const allServicesActive =
                      group.services.length > 0 &&
                      group.activeCount === group.services.length;
                    const categoryCommission = String(
                      categoryCommissionInputs[group.key] ?? "",
                    );

                    return (
                      <div
                        key={group.key}
                        className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
                      >
                        <div className="bg-slate-50 px-4 py-3">
                          <button
                            type="button"
                            onClick={() => toggleCategoryCollapse(group.key)}
                            className="flex w-full items-center justify-between gap-3 text-left"
                          >
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-slate-900">
                                {group.name}
                              </p>
                              <p className="text-[11px] text-slate-500">
                                {group.activeCount} ativo(s) de{" "}
                                {group.services.length}
                              </p>
                            </div>
                            <span className="shrink-0 text-[11px] font-semibold text-slate-600">
                              {isCollapsed ? "Mostrar" : "Ocultar"}
                            </span>
                          </button>

                          {/* Comissão da categoria inteira — evita repetir o
                              mesmo percentual serviço a serviço. No celular
                              (o app roda em Capacitor) as ações empilham e o
                              botão ocupa a linha toda: alvo de toque grande e
                              sem estourar a largura. */}
                          <div className="mt-3 flex flex-col gap-2 border-t border-slate-200 pt-3 sm:flex-row sm:items-center sm:justify-between">
                            {/* Switch e rótulo são irmãos, não aninhados: o
                                PrettySwitch já é um <button>, e um dentro do
                                outro faria o clique alternar duas vezes. */}
                            <div className="flex min-h-[44px] items-center gap-2">
                              <PrettySwitch
                                checked={allServicesActive}
                                onChange={() =>
                                  toggleCategoryServices(
                                    group,
                                    !allServicesActive,
                                  )
                                }
                                size="sm"
                                ariaLabel={`Ativar todos os serviços de ${group.name}`}
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  toggleCategoryServices(
                                    group,
                                    !allServicesActive,
                                  )
                                }
                                className="py-2 text-xs font-medium text-slate-600"
                              >
                                Ativar todos
                              </button>
                            </div>

                            <div className="flex items-stretch gap-2">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                inputMode="decimal"
                                placeholder="%"
                                aria-label={`Comissão para os serviços de ${group.name}`}
                                value={categoryCommission}
                                onChange={(event) =>
                                  handleCategoryCommissionInput(
                                    group.key,
                                    event.target.value,
                                  )
                                }
                                className="h-11 w-20 shrink-0 rounded-xl border border-slate-300 px-3 text-sm text-slate-900 focus:border-rose-400 focus:ring-rose-300"
                              />
                              <Button
                                type="button"
                                size="sm"
                                color="light"
                                className="h-11 flex-1 whitespace-nowrap sm:flex-none"
                                disabled={
                                  !group.activeCount ||
                                  categoryCommission.trim() === ""
                                }
                                onClick={() => applyCategoryCommission(group)}
                              >
                                Aplicar na categoria
                              </Button>
                            </div>
                          </div>

                          <p className="mt-2 text-[11px] leading-snug text-slate-400">
                            {group.activeCount
                              ? `Aplica a comissão padrão nos ${group.activeCount} serviço(s) ativo(s) desta categoria.`
                              : "Ative os serviços da categoria para aplicar a comissão."}
                          </p>
                        </div>

                        {!isCollapsed && (
                          <div className="grid gap-3 p-3 xl:grid-cols-2">
                            {group.services.map((service) => {
                              const serviceConfig =
                                serviceCommissionById.get(Number(service.id)) ||
                                {};

                              return (
                                <div
                                  key={service.id}
                                  className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <p className="truncate text-sm font-semibold text-slate-900">
                                        {service.name}
                                      </p>
                                      <p className="text-[11px] text-slate-500">
                                        Base:{" "}
                                        {formatCurrencyBRL.format(
                                          Number(service.price || 0),
                                        )}
                                      </p>
                                    </div>

                                    <PrettySwitch
                                      checked={Boolean(serviceConfig.isCheck)}
                                      onChange={() =>
                                        handleServiceToggle(service.id)
                                      }
                                      size="sm"
                                    />
                                  </div>

                                  <div className="mt-3 grid gap-2 grid-cols-1">
                                    <Input
                                      type="number"
                                      label="Comissão padrão (%)"
                                      step="0.01"
                                      min="0"
                                      value={serviceConfig.commissionPercentage}
                                      onChange={(e) =>
                                        handleServiceCommissionChange(
                                          service.id,
                                          e.target.value,
                                        )
                                      }
                                      disabled={!serviceConfig.isCheck}
                                    />

                                    <Input
                                      type="text"
                                      label="Preço específico (R$)"
                                      value={formatCurrencyInputValue(
                                        serviceConfig.priceUnit,
                                      )}
                                      onChange={(e) =>
                                        handleServiceCommissionChange(
                                          service.id,
                                          parseCurrencyInputValue(
                                            e.target.value,
                                          ),
                                          "priceUnit",
                                        )
                                      }
                                      inputMode="numeric"
                                      placeholder="R$ 0,00"
                                      disabled={!serviceConfig.isCheck}
                                    />

                                    <Input
                                      type="number"
                                      label="Comissão quando for auxiliar (%)"
                                      step="0.01"
                                      min="0"
                                      value={
                                        serviceConfig.commissionPercentageAsAuxiliary
                                      }
                                      onChange={(e) =>
                                        handleServiceCommissionChange(
                                          service.id,
                                          e.target.value,
                                          "commissionPercentageAsAuxiliary",
                                        )
                                      }
                                      disabled={!serviceConfig.isCheck}
                                    />

                                    <Input
                                      type="number"
                                      label="Comissão quando tiver auxiliar (%)"
                                      step="0.01"
                                      min="0"
                                      value={
                                        serviceConfig.commissionPercentageWithAuxiliary
                                      }
                                      onChange={(e) =>
                                        handleServiceCommissionChange(
                                          service.id,
                                          e.target.value,
                                          "commissionPercentageWithAuxiliary",
                                        )
                                      }
                                      disabled={!serviceConfig.isCheck}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-[11px] text-slate-500">
                  Nenhum serviço encontrado para essa busca.
                </p>
              )}
            </section>

            <section className="pt-4 border-t border-slate-200">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <div className="hidden md:flex h-8 w-8 items-center justify-center rounded-full border border-sky-100 bg-sky-50">
                    <FiShoppingBag className="text-sky-700 text-base" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500">
                      Comissão por produto
                    </p>
                    <p className="text-[11px] text-gray-500">
                      {totalActiveProducts} ativo(s)
                    </p>
                  </div>
                </div>
                <span className="rounded-full bg-sky-50 px-2.5 py-1 text-[11px] font-semibold text-sky-700">
                  {groupedProducts.reduce(
                    (acc, group) => acc + group.products.length,
                    0,
                  )}{" "}
                  total
                </span>
              </div>

              <p className="text-[11px] text-gray-500 mb-3">
                Ative os produtos vendidos por este profissional e ajuste a
                comissão e o preço específico de cada um.
              </p>

              <div className="mb-3">
                <Input
                  type="text"
                  label="Buscar produto"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                />
              </div>

              {isLoadingProducts ? (
                <div className="text-[11px] text-slate-500 flex items-center gap-1">
                  <AiOutlineLoading className="h-3 w-3 animate-spin" />
                  Carregando produtos...
                </div>
              ) : groupedProducts.length > 0 ? (
                <div className="space-y-3 pr-1">
                  {groupedProducts.map((group) => {
                    const isCollapsed = Boolean(collapsedCategories[group.key]);

                    return (
                      <div
                        key={group.key}
                        className="rounded-xl border border-slate-200 bg-slate-50"
                      >
                        <button
                          type="button"
                          onClick={() => toggleCategoryCollapse(group.key)}
                          className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left"
                        >
                          <div>
                            <p className="text-sm font-semibold text-slate-900">
                              {group.name}
                            </p>
                            <p className="text-[11px] text-slate-500">
                              {group.activeCount} ativo(s) de{" "}
                              {group.products.length}
                            </p>
                          </div>
                          <span className="text-[11px] font-semibold text-slate-600">
                            {isCollapsed ? "Mostrar" : "Ocultar"}
                          </span>
                        </button>

                        {!isCollapsed && (
                          <div className="space-y-2 px-2 pb-2">
                            {group.products.map((product) => {
                              const productConfig =
                                productCommissionById.get(Number(product.id)) ||
                                {};

                              return (
                                <div
                                  key={product.id}
                                  className="rounded-xl border border-slate-200 bg-white p-3"
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                      <p className="text-sm font-semibold text-slate-900 truncate">
                                        {product.name}
                                      </p>
                                      <p className="text-[11px] text-slate-500">
                                        Valor base: R${" "}
                                        {Number(product.price || 0).toFixed(2)}
                                      </p>
                                    </div>

                                    <div className="ml-2">
                                      <PrettySwitch
                                        checked={Boolean(productConfig.isCheck)}
                                        onChange={() =>
                                          handleProductToggle(product.id)
                                        }
                                        size="sm"
                                      />
                                    </div>
                                  </div>

                                  <div className="mt-3">
                                    <Input
                                      type="number"
                                      label="Comissão desse produto (%)"
                                      step="0.01"
                                      min="0"
                                      value={productConfig.commissionPercentage}
                                      onChange={(e) =>
                                        handleProductCommissionChange(
                                          product.id,
                                          e.target.value,
                                        )
                                      }
                                      disabled={!productConfig.isCheck}
                                    />
                                  </div>

                                  <div className="mt-3">
                                    <Input
                                      type="text"
                                      label="Preço específico (R$)"
                                      value={formatCurrencyInputValue(
                                        productConfig.priceUnit,
                                      )}
                                      onChange={(e) =>
                                        handleProductValueChange(
                                          product.id,
                                          parseCurrencyInputValue(
                                            e.target.value,
                                          ),
                                          "priceUnit",
                                        )
                                      }
                                      inputMode="numeric"
                                      placeholder="R$ 0,00"
                                      disabled={!productConfig.isCheck}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-[11px] text-slate-500">
                  Nenhum produto encontrado para essa busca.
                </p>
              )}
            </section>
          </>
        )}
      </div>

      {/* BOTÃO SALVAR — ancorado na base da área rolável do modal */}
      <div className="sticky bottom-0 -mx-5 -mb-4 mt-5 space-y-2 border-t border-slate-100 bg-white px-5 py-3">
        <Button
          type="submit"
          className="w-full primary"
          isProcessing={isSubmitting}
          processingSpinner={
            <AiOutlineLoading className="h-5 w-5 animate-spin" />
          }
          disabled={isSubmitting}
        >
          {type === "CREATE" ? "Criar profissional" : "Atualizar profissional"}
        </Button>

        {errorMessage && (
          <Alert color="failure" icon={HiInformationCircle}>
            {errorMessage}
          </Alert>
        )}
      </div>
    </form>
  );
};

export default ProfessionalForm;
